import { buttonVariants } from "@foreday/ui/components/button-variants";
import { EmptyState } from "@foreday/ui/components/empty-state";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { PageFailed } from "@/client/components/route-states";
import { TableShapeSkeleton } from "@/client/components/table-skeleton";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { holidaysQuery, scheduleQuery } from "@/client/lib/schedule-query";
import { ensureTenantData, requireModule } from "@/client/lib/session";
import { useNow } from "@/client/lib/use-now";
import {
  leaveOffQuery,
  leaveTypesQuery,
  todayQuery,
} from "@/features/attendance/attendance-query";
import {
  ChangeTimesDialog,
  type TimesToChange,
} from "@/features/attendance/components/change-times-dialog";
import { CompanyDay } from "@/features/attendance/components/company-day";
import { attendanceCopy } from "@/features/attendance/copy";
import { companyDay, shownOf } from "@/features/attendance/day-groups";
import { changesHoursOf, setsOthersHours } from "@/features/attendance/roles";
import { dayIn } from "@/features/attendance/rules";
import {
  attendanceListSearch,
  type DayShown,
} from "@/features/attendance/schemas";
import { useChangeDayTimes } from "@/features/attendance/use-day-writes";
import { peopleQuery } from "@/features/people/people-query";

/** Nothing past today has anybody's day, so an address naming one opens on today. */
const shownDay = (asked: string | undefined, today: string) =>
  asked && asked < today ? asked : today;

/** An open day's hours run to now, so the list re-reads the clock twice a minute. */
const TICK = 30_000;

export const Route = createFileRoute("/_authenticated/$slug/attendance/")({
  pendingMs: PENDING_MS,
  // A missing day is today in the company's timezone, never the browser's, so it is filled here
  // rather than by the schema.
  validateSearch: attendanceListSearch,
  loaderDeps: ({ search }) => ({ day: search.day }),
  beforeLoad: ({ context, params }) => {
    if (
      !context.companyModules.includes("attendance") &&
      context.companyModules.includes("leave")
    ) {
      throw redirect({ to: "/$slug/attendance/leave", params });
    }
    requireModule(context, "attendance");
    // An Employee has no Today tab: their Attendance opens on their own month.
    if (!setsOthersHours(context.role)) {
      throw redirect({ to: "/$slug/attendance/my-month", params });
    }
  },
  loader: async ({ context, deps }) => {
    const day = shownDay(deps.day, dayIn(context.companyTimezone, new Date()));
    const leaveOn = context.companyModules.includes("leave");
    await Promise.all([
      ensureTenantData(context.queryClient, todayQuery(day)),
      ensureTenantData(context.queryClient, scheduleQuery),
      ensureTenantData(context.queryClient, leaveOffQuery(day, day, leaveOn)),
      ensureTenantData(context.queryClient, leaveTypesQuery(leaveOn)),
      ensureTenantData(
        context.queryClient,
        holidaysQuery(Number(day.slice(0, 4))),
      ),
      // The viewer's own row, and who set a time after the fact.
      ensureTenantData(context.queryClient, peopleQuery),
    ]);
  },
  head: ({ match }) => ({
    meta: [
      { title: pageTitle(attendanceCopy.title, match.context.companyName) },
    ],
  }),
  pendingComponent: () => <TableShapeSkeleton />,
  errorComponent: PageFailed,
  component: Attendance,
});

function Attendance() {
  const search = Route.useSearch();
  const { companyTimezone, companyModules, email, role } =
    Route.useRouteContext();
  const now = useNow(TICK);
  const today = dayIn(companyTimezone, now);
  const day = shownDay(search.day, today);
  const leaveOn = companyModules.includes("leave");

  const { data: rows } = useSuspenseQuery(todayQuery(day));
  const { data: schedule } = useSuspenseQuery(scheduleQuery);
  const { data: leave } = useSuspenseQuery(leaveOffQuery(day, day, leaveOn));
  const { data: types } = useSuspenseQuery(leaveTypesQuery(leaveOn));
  const { data: holidays } = useSuspenseQuery(
    holidaysQuery(Number(day.slice(0, 4))),
  );
  const { data: people } = useSuspenseQuery(peopleQuery);
  // The session carries an email, not a person id, so the viewer is found by email, compared
  // case-insensitively as the invitation lookup and account link do.
  const asked = email?.toLowerCase();
  const viewer = people.find(
    (row) => row.kind === "person" && row.email.toLowerCase() === asked,
  );

  const show: DayShown | undefined = search.show;
  const changeTimes = useChangeDayTimes(day);
  const [changing, setChanging] = useState<TimesToChange | null>(null);
  const view = shownOf(
    companyDay({
      day,
      rows,
      schedule,
      holidays,
      leave,
      timeZone: companyTimezone,
      now,
    }),
    show,
  );

  return (
    <>
      {view.kind === "list" && show && view.groups.length === 0 ? (
        <EmptyState
          kind="filtered-out"
          className="pt-16"
          title={attendanceCopy.day.noneShown[show]}
          sentence={null}
          action={
            <Link
              to="."
              search={(prev: object) => ({ ...prev, show: undefined })}
              className={buttonVariants({ variant: "secondary" })}
            >
              {attendanceCopy.day.showEveryone}
            </Link>
          }
        />
      ) : null}
      <CompanyDay
        view={view}
        day={day}
        timeZone={companyTimezone}
        viewerPersonId={viewer?.id ?? null}
        names={new Map(people.map((person) => [person.id, person.name]))}
        leaveTypes={new Map(types.map((type) => [type.id, type.name]))}
        changes={(personId) =>
          changesHoursOf(
            { personId: viewer?.id ?? null, role },
            personId,
            people,
          )
        }
        onChangeTimes={(line) => {
          if (line.missed) {
            setChanging({ row: line.row, day, entry: line.missed });
          }
        }}
      />
      <ChangeTimesDialog
        item={changing}
        timeZone={companyTimezone}
        onClose={() => setChanging(null)}
        onSave={(times) => {
          const row = rows.find(
            (held) => held.personId === changing?.entry.personId,
          );
          if (row && changing) {
            changeTimes({ row, entryId: changing.entry.id, times });
          }
          setChanging(null);
        }}
      />
    </>
  );
}
